import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PaymentsIcon from '@mui/icons-material/Payments';
import PercentIcon from '@mui/icons-material/Percent';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import moment from 'moment';
import BookingExtraChargesBreakdown from '../../../components/booking/BookingExtraChargesBreakdown';
import { updateHallBookingFinancials } from '../api';
import {
  computeHallLocalPaymentSummary,
  formatHallCurrency,
  paymentModeLabel,
  resolveHallPaymentSummary,
  resolveLocalDiscountAmount,
  roundHallMoney,
} from '../utils/hallFinancials';
import { getHallFormInputSx } from '../utils/hallDrawerStyles';
import { getHallApiErrorMessage } from '../utils/hallErrors';

const HALL_ACCENT = '#4ce1b6';

const HALL_DISCOUNT_QUICK_CHIPS = {
  percentage: [
    { label: 'Early bird 5%', value: 5 },
    { label: 'Promo 10%', value: 10 },
    { label: 'Corporate 15%', value: 15 },
    { label: 'Special 20%', value: 20 },
    { label: 'VIP 25%', value: 25 },
    { label: 'Partner 30%', value: 30 },
    { label: 'Half off 50%', value: 50 },
  ],
  amount: [
    { label: 'Flat ₹500 off', value: 500 },
    { label: 'Flat ₹1,000 off', value: 1000 },
    { label: 'Flat ₹2,000 off', value: 2000 },
    { label: 'Flat ₹5,000 off', value: 5000 },
    { label: 'Flat ₹10,000 off', value: 10000 },
    { label: 'Flat ₹25,000 off', value: 25000 },
  ],
};

function LineRow({ label, value, muted, emphasize, isDark }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.45 }}>
      <Typography
        variant="body2"
        sx={{
          color: muted ? (isDark ? '#94a3b8' : '#64748b') : (isDark ? '#cbd5e1' : '#334155'),
          fontWeight: emphasize ? 800 : 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: emphasize ? HALL_ACCENT : (isDark ? '#e2e8f0' : '#0f172a'),
          fontWeight: emphasize ? 800 : 700,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** Applied discounts list — matches Extra Charges breakdown styling. */
function HallDiscountsBreakdown({
  discounts = [],
  discountBase = 0,
  isDark = false,
  accent = HALL_ACCENT,
  onRemoveItem,
  removeDisabled = false,
}) {
  if (!discounts.length) return null;

  const rows = discounts.map((item) => {
    const type = String(item.type || 'amount').toLowerCase();
    const value = roundHallMoney(item.value != null ? item.value : item.amount);
    const amount = resolveLocalDiscountAmount(item, discountBase);
    return {
      id: item.id,
      label: item.label || (type === 'percentage' ? `${value}% discount` : 'Discount'),
      type,
      value,
      amount,
      detail: type === 'percentage' ? `${value}%` : formatHallCurrency(value),
    };
  });
  const total = roundHallMoney(rows.reduce((sum, row) => sum + row.amount, 0));
  const accentColor = isDark ? '#fde68a' : '#92400e';
  const canRemove = typeof onRemoveItem === 'function';

  return (
    <Box
      sx={{
        mt: 0.5,
        mb: 0.5,
        p: 1,
        borderRadius: 2,
        border: `1px dashed ${alpha(accent, isDark ? 0.35 : 0.28)}`,
        backgroundColor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.04),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 800,
          color: accent,
          mb: 0.85,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        Discounts
      </Typography>

      {rows.map((row, index) => (
        <Box
          key={row.id || `${row.label}-${index}`}
          sx={{
            mb: index < rows.length - 1 ? 0.65 : rows.length > 1 ? 0.75 : 0,
            pb: index < rows.length - 1 ? 0.55 : 0,
            borderBottom:
              index < rows.length - 1
                ? `1px solid ${alpha(accent, isDark ? 0.16 : 0.12)}`
                : 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                {row.label}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                {row.type === 'percentage' ? `Rate ${row.detail}` : `Amount ${row.detail}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, color: accentColor, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
              >
                - {formatHallCurrency(row.amount)}
              </Typography>
              {canRemove ? (
                <IconButton
                  size="small"
                  onClick={() => onRemoveItem(row)}
                  disabled={removeDisabled}
                  aria-label={`Remove ${row.label}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        </Box>
      ))}

      {rows.length > 1 ? (
        <>
          <Divider sx={{ my: 0.75, borderColor: alpha(accent, isDark ? 0.2 : 0.14) }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Discount total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: accentColor }}>
              - {formatHallCurrency(total)}
            </Typography>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

export default function HallEventBillingSection({
  bookingId,
  booking,
  payload,
  isDark,
  locked = false,
  panelCardSx,
  onUpdated,
  onNotice,
  onAddExtra,
  onEditExtra,
  onCollectPayment,
}) {
  const summary = useMemo(
    () => resolveHallPaymentSummary(payload, booking),
    [payload, booking],
  );
  const inputSx = getHallFormInputSx(isDark);

  const [extraCharges, setExtraCharges] = useState(summary.financial_info.extra_charges);
  const [discounts, setDiscounts] = useState(summary.financial_info.discounts);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setExtraCharges(summary.financial_info.extra_charges || []);
    setDiscounts(summary.financial_info.discounts || []);
  }, [summary]);

  const liveSummary = useMemo(
    () => computeHallLocalPaymentSummary(summary, { extraCharges, discounts }),
    [summary, extraCharges, discounts],
  );

  const discountBase = useMemo(
    () => roundHallMoney(Number(liveSummary.base_amount || 0) + Number(liveSummary.extras_base_amount || 0)),
    [liveSummary.base_amount, liveSummary.extras_base_amount],
  );

  const discountPreviewAmount = useMemo(() => {
    const value = roundHallMoney(discountValue);
    if (value <= 0) return 0;
    if (discountType === 'percentage') {
      return roundHallMoney((discountBase * Math.min(100, value)) / 100);
    }
    return Math.min(value, discountBase);
  }, [discountBase, discountType, discountValue]);

  const dirty = useMemo(() => {
    const extrasChanged = JSON.stringify(extraCharges) !== JSON.stringify(summary.financial_info.extra_charges || []);
    const discountsChanged = JSON.stringify(discounts) !== JSON.stringify(summary.financial_info.discounts || []);
    return extrasChanged || discountsChanged;
  }, [discounts, extraCharges, summary]);

  const persistFinancials = useCallback(async (nextExtras, nextDiscounts, { silent = false } = {}) => {
    if (!bookingId || locked) return false;
    setSaving(true);
    setError('');
    try {
      const result = await updateHallBookingFinancials(bookingId, {
        extra_charges: (nextExtras || []).map((item) => ({
          id: String(item.id || '').startsWith('local-') ? undefined : item.id,
          label: item.label,
          base_amount: item.base_amount,
          gst_percent: item.gst_percent,
          gst_amount: item.gst_amount,
          total: item.total,
          created_at: item.created_at,
        })),
        discounts: (nextDiscounts || []).map((item) => ({
          id: String(item.id || '').startsWith('local-') ? undefined : item.id,
          label: item.label,
          type: item.type || 'amount',
          value: item.value != null ? item.value : item.amount,
          amount: item.amount,
          created_at: item.created_at,
        })),
      });
      onUpdated?.(result);
      if (!silent) {
        onNotice?.('success', 'Billing details updated.');
      }
      return true;
    } catch (err) {
      setError(getHallApiErrorMessage(err, 'Failed to update billing'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [bookingId, locked, onNotice, onUpdated]);

  const handleAddDiscount = async () => {
    const value = roundHallMoney(discountValue);
    if (value <= 0) {
      setError(discountType === 'percentage'
        ? 'Discount percentage must be greater than zero.'
        : 'Discount amount must be greater than zero.');
      return;
    }
    if (discountType === 'percentage' && value > 100) {
      setError('Discount percentage cannot exceed 100%.');
      return;
    }
    const amount = resolveLocalDiscountAmount({ type: discountType, value }, discountBase);
    if (amount <= 0) {
      setError('Discount resolves to zero for the current booking total.');
      return;
    }
    const matchedChip = (HALL_DISCOUNT_QUICK_CHIPS[discountType] || []).find(
      (chip) => roundHallMoney(chip.value) === value,
    );
    const label = matchedChip?.label
      || (discountType === 'percentage' ? `${value}% discount` : `${formatHallCurrency(value)} off`);
    const nextDiscounts = [
      ...discounts,
      {
        id: `local-discount-${Date.now()}`,
        label,
        type: discountType,
        value,
        amount,
      },
    ];
    setError('');
    setDiscounts(nextDiscounts);
    setDiscountValue('');
    const ok = await persistFinancials(extraCharges, nextDiscounts, { silent: true });
    if (ok) {
      onNotice?.('success', 'Discount applied.');
    }
  };

  const handleRemoveDiscount = async (discountId) => {
    const nextDiscounts = discounts.filter((row) => row.id !== discountId);
    setDiscounts(nextDiscounts);
    const ok = await persistFinancials(extraCharges, nextDiscounts, { silent: true });
    if (ok) {
      onNotice?.('success', 'Discount removed.');
    }
  };

  const handleRemoveExtra = async (extraId) => {
    const nextExtras = extraCharges.filter((row) => row.id !== extraId);
    setExtraCharges(nextExtras);
    const ok = await persistFinancials(nextExtras, discounts, { silent: true });
    if (ok) {
      onNotice?.('success', 'Extra charge removed.');
    }
  };

  const handleSave = async () => {
    await persistFinancials(extraCharges, discounts);
  };

  const showExtras = extraCharges.length > 0
    || Number(liveSummary.extras_base_amount || 0) > 0
    || Number(liveSummary.extras_total || 0) > 0;
  const showDiscount = Number(liveSummary.discount_amount || 0) > 0;
  const showEventGst = Number(liveSummary.gst_amount || 0) > 0;

  return (
    <Box sx={panelCardSx}>
      <Box sx={{ p: { xs: 1.6, sm: 1.9 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReceiptLongIcon sx={{ color: HALL_ACCENT, fontSize: 22 }} />
              Event Payment Summary
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 0.35 }}>
              Extras use their own GST. Event GST applies only on hall rental after discount.
            </Typography>
          </Box>
          {dirty && !locked ? (
            <Button
              variant="outlined"
              onClick={handleSave}
              disabled={saving}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                color: HALL_ACCENT,
                borderColor: alpha(HALL_ACCENT, 0.5),
                alignSelf: 'flex-start',
              }}
            >
              {saving ? 'Saving...' : 'Save billing'}
            </Button>
          ) : null}
        </Box>

        {locked ? (
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
            Billing is locked for completed or cancelled events.
          </Alert>
        ) : null}

        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${isDark ? alpha('#64748b', 0.35) : alpha('#cbd5e1', 0.7)}`,
            backgroundColor: isDark ? alpha('#0f172a', 0.4) : alpha('#f8fafc', 0.95),
          }}
        >
          <LineRow label="Hall rental" value={formatHallCurrency(liveSummary.base_amount)} isDark={isDark} />
          {showExtras ? (
            <BookingExtraChargesBreakdown
              items={extraCharges}
              amount={liveSummary.extras_total}
              baseAmount={liveSummary.extras_base_amount}
              gstAmount={liveSummary.extras_gst_amount}
              currency="INR"
              isDark={isDark}
              accent={HALL_ACCENT}
              compact
              onEditItem={!locked ? (row) => {
                const match = extraCharges.find((item) => String(item.id) === String(row.id))
                  || extraCharges[row.itemIndex]
                  || null;
                if (match) onEditExtra?.(match);
              } : undefined}
              onRemoveItem={!locked ? (row) => handleRemoveExtra(row.id) : undefined}
              removeDisabled={saving}
            />
          ) : null}
          {(showExtras || showDiscount) ? (
            <LineRow
              label="Subtotal (excl. extras GST)"
              value={formatHallCurrency(liveSummary.subtotal_before_discount)}
              isDark={isDark}
            />
          ) : null}
          {showDiscount ? (
            <HallDiscountsBreakdown
              discounts={discounts}
              discountBase={discountBase}
              isDark={isDark}
              accent={HALL_ACCENT}
              onRemoveItem={!locked ? (row) => handleRemoveDiscount(row.id) : undefined}
              removeDisabled={saving}
            />
          ) : null}
          {showDiscount ? (
            <LineRow
              label="After discount"
              value={formatHallCurrency(liveSummary.subtotal_after_discount)}
              isDark={isDark}
            />
          ) : null}
          {(showEventGst || Number(liveSummary.event_taxable_amount || 0) > 0) ? (
            <LineRow
              label="Taxable amount (hall after discount)"
              value={formatHallCurrency(liveSummary.event_taxable_amount)}
              isDark={isDark}
            />
          ) : null}
          {showEventGst ? (
            <LineRow
              label={`Event GST (${liveSummary.gst_percent}%)`}
              value={formatHallCurrency(liveSummary.gst_amount)}
              muted
              isDark={isDark}
            />
          ) : null}
          {Number(liveSummary.vendor_included_total || 0) > 0 ? (
            <LineRow
              label="Vendor (included in hall)"
              value={formatHallCurrency(liveSummary.vendor_included_total)}
              isDark={isDark}
            />
          ) : null}
          <LineRow label="Grand total" value={formatHallCurrency(liveSummary.grand_total)} emphasize isDark={isDark} />
          {!locked ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 0.5 }}>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={onAddExtra}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: HALL_ACCENT,
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  p: 0,
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add Extra Charges
              </Link>
            </Box>
          ) : null}
          <Divider sx={{ my: 0.75, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />
          <LineRow label="Paid (guest)" value={formatHallCurrency(liveSummary.paid_amount)} isDark={isDark} />
          <LineRow label="Due (guest)" value={formatHallCurrency(liveSummary.due_amount)} emphasize isDark={isDark} />
          {Number(liveSummary.vendor_paid_total || 0) > 0 || Number(liveSummary.vendor_due_total || 0) > 0 ? (
            <>
              <LineRow
                label="Vendor payouts"
                value={formatHallCurrency(liveSummary.vendor_paid_total)}
                isDark={isDark}
              />
              <LineRow
                label="Vendor due"
                value={formatHallCurrency(liveSummary.vendor_due_total)}
                isDark={isDark}
              />
              <LineRow
                label="Hotel net collection"
                value={formatHallCurrency(liveSummary.hotel_net_collection)}
                emphasize
                isDark={isDark}
              />
            </>
          ) : null}
          {!locked && Number(liveSummary.due_amount || 0) > 0 && typeof onCollectPayment === 'function' ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<PaymentsIcon />}
                onClick={onCollectPayment}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: HALL_ACCENT,
                  color: '#04120e',
                  '&:hover': { bgcolor: HALL_ACCENT, opacity: 0.92 },
                }}
              >
                Collect payment
              </Button>
            </Box>
          ) : null}
        </Box>

        {(summary.financial_info.payments || []).length > 0 ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: `1px solid ${isDark ? alpha('#64748b', 0.35) : alpha('#cbd5e1', 0.7)}`,
              backgroundColor: isDark ? alpha('#0f172a', 0.35) : alpha('#fff', 0.9),
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: isDark ? '#f8fafc' : '#0f172a',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 1.25,
              }}
            >
              <PaymentsIcon sx={{ fontSize: 18, color: HALL_ACCENT }} />
              Payment history ({summary.financial_info.payments.length})
            </Typography>
            <Stack spacing={1}>
              {[...summary.financial_info.payments].reverse().map((payment) => (
                <Box
                  key={payment.id || `${payment.paid_at}-${payment.amount}`}
                  sx={{
                    p: 1.15,
                    borderRadius: 1.5,
                    border: `1px solid ${isDark ? alpha('#64748b', 0.3) : alpha('#cbd5e1', 0.75)}`,
                    backgroundColor: isDark ? alpha('#0b1220', 0.55) : '#fff',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 800, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                      {formatHallCurrency(payment.amount)}
                    </Typography>
                    <Chip
                      size="small"
                      label={paymentModeLabel(payment.mode || payment.payment_method)}
                      sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem' }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    {payment.paid_at || payment.processed_at
                      ? moment(payment.paid_at || payment.processed_at).format('DD MMM YYYY, hh:mm A')
                      : '—'}
                    {payment.reference || payment.reference_number
                      ? ` · ${payment.reference || payment.reference_number}`
                      : ''}
                    {payment.payment_type_label || payment.payment_type
                      ? ` · ${payment.payment_type_label || String(payment.payment_type).replace(/_/g, ' ')}`
                      : ''}
                  </Typography>
                  {payment.note || payment.notes ? (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: isDark ? '#cbd5e1' : '#475569' }}>
                      {payment.note || payment.notes}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Box>
        ) : null}

        {!locked ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: `1px solid ${isDark ? alpha('#64748b', 0.35) : alpha('#cbd5e1', 0.7)}`,
              background: isDark
                ? `linear-gradient(145deg, ${alpha(HALL_ACCENT, 0.1)} 0%, ${alpha('#0f172a', 0.55)} 100%)`
                : `linear-gradient(145deg, ${alpha(HALL_ACCENT, 0.1)} 0%, ${alpha('#fff', 0.95)} 100%)`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                <LocalOfferIcon sx={{ fontSize: 18, color: HALL_ACCENT }} />
                Add Discount
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                Base {formatHallCurrency(discountBase)}
              </Typography>
            </Box>

            <Stack spacing={1.25}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={discountType}
                onChange={(_, value) => {
                  if (!value) return;
                  setDiscountType(value);
                  setDiscountValue('');
                }}
                sx={{
                  border: `1px solid ${isDark ? alpha('#64748b', 0.45) : alpha('#cbd5e1', 0.9)}`,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: 'transparent',
                    color: isDark ? '#e2e8f0' : '#334155',
                    backgroundColor: isDark ? alpha('#0f172a', 0.45) : '#ffffff',
                    px: 1.45,
                    py: 0.55,
                    minHeight: 38,
                  },
                  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                    marginLeft: 0,
                    borderLeft: `1px solid ${isDark ? alpha('#64748b', 0.45) : alpha('#cbd5e1', 0.9)}`,
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${HALL_ACCENT} !important`,
                    color: '#04120e !important',
                  },
                }}
              >
                <ToggleButton value="percentage">
                  <PercentIcon sx={{ fontSize: 16, mr: 0.6 }} />
                  Percentage (%)
                </ToggleButton>
                <ToggleButton value="amount">
                  <CurrencyRupeeIcon sx={{ fontSize: 16, mr: 0.6 }} />
                  Amount (INR)
                </ToggleButton>
              </ToggleButtonGroup>

              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}
                >
                  Quick discounts
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {(HALL_DISCOUNT_QUICK_CHIPS[discountType] || []).map((chip) => {
                    const selected = roundHallMoney(discountValue) === roundHallMoney(chip.value);
                    return (
                      <Chip
                        key={`${discountType}-${chip.value}-${chip.label}`}
                        size="small"
                        clickable
                        label={chip.label}
                        onClick={() => {
                          setDiscountValue(String(chip.value));
                        }}
                        sx={{
                          height: 28,
                          fontWeight: 700,
                          fontSize: '0.74rem',
                          borderRadius: 1.5,
                          color: selected ? '#04120e' : (isDark ? HALL_ACCENT : '#0f766e'),
                          bgcolor: selected
                            ? HALL_ACCENT
                            : alpha(HALL_ACCENT, isDark ? 0.16 : 0.12),
                          border: `1px solid ${selected
                            ? HALL_ACCENT
                            : alpha(HALL_ACCENT, isDark ? 0.35 : 0.28)}`,
                          '&:hover': {
                            bgcolor: selected
                              ? HALL_ACCENT
                              : alpha(HALL_ACCENT, isDark ? 0.24 : 0.18),
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
                <TextField
                  label={discountType === 'percentage' ? 'Discount %' : 'Discount amount'}
                  type="number"
                  value={discountValue}
                  onChange={(event) => setDiscountValue(event.target.value)}
                  fullWidth
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {discountType === 'percentage' ? '%' : 'INR'}
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    discountPreviewAmount > 0
                      ? `Preview save: ${formatHallCurrency(discountPreviewAmount)}`
                      : 'Enter a value to preview savings'
                  }
                />
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={handleAddDiscount}
                  disabled={saving}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    color: '#04120e',
                    backgroundColor: HALL_ACCENT,
                    whiteSpace: 'nowrap',
                    minHeight: 48,
                    px: 2,
                    boxShadow: `0 8px 18px ${alpha(HALL_ACCENT, 0.28)}`,
                    '&:hover': { backgroundColor: HALL_ACCENT, opacity: 0.92 },
                  }}
                >
                  Add discount
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}
      </Box>
    </Box>
  );
}
